import { useState } from 'react';
import { downloadPdf } from '../services/api';

interface ExportButtonsProps {
  note: string;
  patientName: string;
  disabled: boolean;
}

export default function ExportButtons({ note, patientName, disabled }: ExportButtonsProps) {
  const [copySuccess, setCopySuccess] = useState(false);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [pdfError, setPdfError] = useState<string | null>(null);

  const handleCopyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(note);
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const handleExportPdf = async () => {
    setPdfLoading(true);
    setPdfError(null);
    try {
      await downloadPdf(note, patientName);
    } catch (err) {
      console.error('Failed to export PDF:', err);
      setPdfError('Failed to generate PDF');
      setTimeout(() => setPdfError(null), 3000);
    } finally {
      setPdfLoading(false);
    }
  };

  const handlePrint = () => {
    const printWindow = window.open('', '_blank');
    if (printWindow) {
      printWindow.document.write(`
        <!DOCTYPE html>
        <html>
          <head>
            <title>Psychiatric Note - ${patientName || 'Patient'}</title>
            <style>
              body {
                font-family: 'Courier New', Consolas, monospace;
                font-size: 11px;
                line-height: 1.4;
                padding: 20px;
                max-width: 800px;
                margin: 0 auto;
              }
              pre {
                white-space: pre-wrap;
                word-wrap: break-word;
              }
              @media print {
                body { padding: 0; }
              }
            </style>
          </head>
          <body>
            <pre>${note}</pre>
          </body>
        </html>
      `);
      printWindow.document.close();
      printWindow.focus();
      setTimeout(() => {
        printWindow.print();
      }, 250);
    }
  };

  return (
    <div className="flex flex-wrap gap-3">
      {/* Copy to Clipboard */}
      <button
        onClick={handleCopyToClipboard}
        disabled={disabled}
        className={`btn-secondary flex items-center gap-2 ${
          copySuccess ? 'bg-green-100 text-green-700' : ''
        }`}
      >
        {copySuccess ? (
          <>
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
            Copied!
          </>
        ) : (
          <>
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
            </svg>
            Copy to Clipboard
          </>
        )}
      </button>

      {/* Export as PDF */}
      <button
        onClick={handleExportPdf}
        disabled={disabled || pdfLoading}
        className={`btn-secondary flex items-center gap-2 ${
          pdfError ? 'bg-red-100 text-red-700' : ''
        }`}
      >
        {pdfLoading ? (
          <>
            <div className="w-5 h-5 border-2 border-gray-300 border-t-gray-600 rounded-full animate-spin"></div>
            Generating...
          </>
        ) : pdfError ? (
          <>
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
            {pdfError}
          </>
        ) : (
          <>
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            Export as PDF
          </>
        )}
      </button>

      {/* Print */}
      <button
        onClick={handlePrint}
        disabled={disabled}
        className="btn-secondary flex items-center gap-2"
      >
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
        </svg>
        Print
      </button>
    </div>
  );
}
